import { useEffect, useState, useRef } from 'react';
import { requireArenaSlug } from '@/utils/arena';
import { arenaService } from '@/services/arenaService';
import { facilityService, Facility } from '@/services/facilityService';
import { Icon } from '@iconify/react';

interface DaySchedule {
  isOpen: boolean;
  startTime: string;
  endTime: string;
}

const defaultDays = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];

const defaultSchedules: Record<string, DaySchedule> = {
  Senin: { isOpen: true, startTime: '07:00', endTime: '23:00' },
  Selasa: { isOpen: true, startTime: '07:00', endTime: '23:00' },
  Rabu: { isOpen: true, startTime: '07:00', endTime: '23:00' },
  Kamis: { isOpen: true, startTime: '07:00', endTime: '23:00' },
  Jumat: { isOpen: true, startTime: '07:00', endTime: '23:00' },
  Sabtu: { isOpen: true, startTime: '07:00', endTime: '00:00' },
  Minggu: { isOpen: true, startTime: '07:00', endTime: '00:00' },
};

const defaultFacilities = [
  { value: 'parkir', label: 'Parkir' },
  { value: 'toilet', label: 'Toilet' },
  { value: 'musholla', label: 'Musholla' },
  { value: 'kantin', label: 'Kantin' },
  { value: 'ruang-ganti', label: 'Ruang ganti' },
  { value: 'shower', label: 'Shower' },
  { value: 'wifi', label: 'Wi-Fi' },
  { value: 'tribun', label: 'Tribun' },
  { value: 'ac', label: 'AC' },
  { value: 'loker', label: 'Loker' },
];

const getFacilityIcon = (slugOrName: string) => {
  const s = slugOrName.toLowerCase();
  if (s.includes('parkir')) return 'ph:car-bold';
  if (s.includes('toilet')) return 'ph:toilet-bold';
  if (s.includes('musholla') || s.includes('mushola')) return 'ph:moon-stars-bold';
  if (s.includes('kantin')) return 'ph:fork-knife-bold';
  if (s.includes('ruang-ganti') || s.includes('ruang ganti') || s.includes('ganti')) return 'ph:t-shirt-bold';
  if (s.includes('shower')) return 'ph:shower-bold';
  if (s.includes('wi-fi') || s.includes('wifi')) return 'ph:wifi-high-bold';
  if (s.includes('tribun')) return 'ph:users-three-bold';
  if (s.includes('ac')) return 'ph:snowflake-bold';
  if (s.includes('loker') || s.includes('locker')) return 'ph:lock-key-bold';
  return 'ph:check-circle-bold';
};

interface OperasionalTabProps {
  onSubmit: (values: any, tabId: string) => Promise<void>;
}

const OperasionalTab = ({ onSubmit }: OperasionalTabProps) => {
  const [daySchedules, setDaySchedules] = useState<Record<string, DaySchedule>>(defaultSchedules);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([
    'parkir',
    'toilet',
    'musholla',
    'kantin',
    'ruang-ganti',
  ]);
  const [facilityOptions, setFacilityOptions] = useState<{ value: string; label: string }[]>(defaultFacilities);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [removedExistingImages, setRemovedExistingImages] = useState<number[]>([]);
  const [newImageFiles, setNewImageFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dayMapToId: Record<string, string> = {
    monday: 'Senin',
    tuesday: 'Selasa',
    wednesday: 'Rabu',
    thursday: 'Kamis',
    friday: 'Jumat',
    saturday: 'Sabtu',
    sunday: 'Minggu',
  };

  const dayMapToEn: Record<string, string> = {
    Senin: 'monday',
    Selasa: 'tuesday',
    Rabu: 'wednesday',
    Kamis: 'thursday',
    Jumat: 'friday',
    Sabtu: 'saturday',
    Minggu: 'sunday',
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const slug = requireArenaSlug();
        const arena = await arenaService.getArenaBySlug(slug);
        if (!mounted || !arena) return;

        const opening_hours = (arena as any).opening_hours || {};
        const facilities = (arena as any).facilities?.data || [];
        const images = (arena as any).images?.images || [];

        // Load facility master list
        try {
          const facilityList: Facility[] = await facilityService.getFacilities();
          if (mounted && facilityList?.length) {
            const opts = facilityList.map((f) => ({ value: f.slug, label: f.name }));
            setFacilityOptions(opts);
          }
        } catch (_e) {
          // keep defaults
        }

        // Parse opening hours
        if (Object.keys(opening_hours).length) {
          const nextSchedules: Record<string, DaySchedule> = { ...defaultSchedules };
          defaultDays.forEach((d) => {
            nextSchedules[d] = { ...nextSchedules[d], isOpen: false };
          });

          Object.entries(opening_hours).forEach(([enDay, timeStr]) => {
            const idDay = dayMapToId[enDay.toLowerCase()];
            if (idDay) {
              const [start, end] = String(timeStr).split('-');
              nextSchedules[idDay] = {
                isOpen: true,
                startTime: start || '07:00',
                endTime: end || '23:00',
              };
            }
          });
          setDaySchedules(nextSchedules);
        }

        // Parse facilities
        if (facilities?.length) {
          let slugs: string[] = [];
          if (typeof facilities[0] === 'object' && facilities[0].slug) {
            slugs = facilities.map((f: any) => f.slug);
          } else if (typeof facilities[0] === 'string') {
            slugs = facilities;
          }
          if (slugs.length) {
            setSelectedFacilities(slugs);
          }
        }

        if (images?.length) {
          setExistingImages(images);
        }
      } catch (_err) {
        // ignore
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleToggleDay = (day: string) => {
    setDaySchedules((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        isOpen: !prev[day].isOpen,
      },
    }));
  };

  const handleChangeTime = (day: string, field: 'startTime' | 'endTime', value: string) => {
    setDaySchedules((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const copySeninToAll = () => {
    const senin = daySchedules['Senin'];
    if (!senin) return;
    setDaySchedules((prev) => {
      const next = { ...prev };
      defaultDays.forEach((d) => {
        if (d !== 'Senin') {
          next[d] = {
            ...next[d],
            startTime: senin.startTime,
            endTime: senin.endTime,
          };
        }
      });
      return next;
    });
  };

  const toggleFacility = (slug: string) => {
    setSelectedFacilities((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    try {
      setIsSubmitting(true);
      const slug = requireArenaSlug();
      const formData = new FormData();

      // Opening hours
      const openingPairs = defaultDays
        .filter((day) => daySchedules[day]?.isOpen)
        .map((day) => {
          const enDay = dayMapToEn[day] || day.toLowerCase();
          const s = daySchedules[day];
          return `${enDay}:${s.startTime}-${s.endTime}`;
        });

      if (openingPairs.length) {
        formData.append('opening_hours', openingPairs.join(','));
      }

      // Facilities
      if (selectedFacilities.length) {
        formData.append('facilities', selectedFacilities.join(','));
      }

      // Images
      const keptExisting = existingImages
        .map((src, idx) => ({ src, idx }))
        .filter((_, i) => !removedExistingImages.includes(i))
        .map((o) => o.src);

      formData.append('keep_images', keptExisting.join(','));

      const remainingSlots = Math.max(0, 5 - keptExisting.length);
      for (let i = 0; i < Math.min(newImageFiles.length, remainingSlots); i++) {
        formData.append('images', newImageFiles[i]);
      }

      const res = await arenaService.updateArenaOperational(slug, formData);
      if (res.success) {
        await onSubmit(
          {
            daySchedules,
            selectedFacilities,
          },
          'operasional'
        );
      } else {
        throw new Error(res.message || 'Gagal menyimpan operasional');
      }
    } catch (err: any) {
      console.error('Update operational failed', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="active-profile-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left: JAM OPERASIONAL (Screenshot 3) */}
        <div className="lg:col-span-7 bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">
              JAM OPERASIONAL
            </span>
            <button
              type="button"
              onClick={copySeninToAll}
              className="h-8 px-3.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-xs font-semibold text-[#0E1726] flex items-center gap-1.5 transition-all shadow-2xs cursor-pointer active:scale-[0.98]"
            >
              <Icon icon="ph:copy-bold" className="w-3.5 h-3.5 text-gray-500" />
              <span>Samakan dengan Senin</span>
            </button>
          </div>

          <div className="space-y-2.5 pt-1">
            {defaultDays.map((day) => {
              const schedule = daySchedules[day] || {
                isOpen: true,
                startTime: '07:00',
                endTime: '23:00',
              };

              return (
                <div
                  key={day}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between p-2.5 sm:px-4 sm:py-2.5 rounded-xl border transition-all gap-3 ${
                    schedule.isOpen
                      ? 'border-gray-100 bg-white hover:border-gray-200'
                      : 'border-transparent bg-gray-50/60 opacity-60'
                  }`}
                >
                  {/* Left: Switch + Day Name */}
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={schedule.isOpen}
                      onClick={() => handleToggleDay(day)}
                      className={`w-11 h-6 rounded-full p-0.5 relative transition-colors cursor-pointer shrink-0 ${
                        schedule.isOpen ? 'bg-[#FC480D]' : 'bg-gray-200'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full transition-transform shadow-2xs ${
                          schedule.isOpen ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                    <span className="text-xs font-semibold text-[#0E1726] w-14">{day}</span>
                  </div>

                  {/* Right: Time Range with Clock Icons */}
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <input
                        type="time"
                        disabled={!schedule.isOpen}
                        value={schedule.startTime}
                        onChange={(e) => handleChangeTime(day, 'startTime', e.target.value)}
                        className="h-9 px-3 pr-8 rounded-xl border border-gray-200 bg-white text-xs font-medium text-[#0E1726] focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors disabled:bg-gray-100 disabled:text-gray-400 w-32 cursor-pointer"
                      />
                      <Icon
                        icon="ph:clock"
                        className="w-4 h-4 text-gray-400 absolute right-2.5 top-2.5 pointer-events-none"
                      />
                    </div>

                    <span className="text-xs text-gray-400 font-medium">-</span>

                    <div className="relative">
                      <input
                        type="time"
                        disabled={!schedule.isOpen}
                        value={schedule.endTime}
                        onChange={(e) => handleChangeTime(day, 'endTime', e.target.value)}
                        className="h-9 px-3 pr-8 rounded-xl border border-gray-200 bg-white text-xs font-medium text-[#0E1726] focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors disabled:bg-gray-100 disabled:text-gray-400 w-32 cursor-pointer"
                      />
                      <Icon
                        icon="ph:clock"
                        className="w-4 h-4 text-gray-400 absolute right-2.5 top-2.5 pointer-events-none"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: FASILITAS (Screenshot 3) */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-4">
          <div className="text-[11px] font-semibold text-gray-400 tracking-wider uppercase">
            FASILITAS
          </div>

          <div className="flex flex-wrap gap-2.5 pt-1">
            {facilityOptions.map((f) => {
              const isSelected = selectedFacilities.includes(f.value);
              const iconName = getFacilityIcon(f.label || f.value);

              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => toggleFacility(f.value)}
                  className={`h-9 px-3.5 rounded-full text-xs font-medium flex items-center gap-2 transition-all cursor-pointer border ${
                    isSelected
                      ? 'bg-[#FFF5EE] text-[#FC480D] border-orange-200/80 font-semibold shadow-2xs'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon
                    icon={iconName}
                    className={`w-4 h-4 ${isSelected ? 'text-[#FC480D]' : 'text-gray-500'}`}
                  />
                  <span>{f.label}</span>
                </button>
              );
            })}
          </div>

          {/* Preserved Foto Arena Section */}
          <div className="pt-4 border-t border-gray-100 space-y-3">
            <span className="text-xs font-semibold text-[#0E1726] block">
              Foto Tambahan Arena
            </span>

            {/* Existing images */}
            {existingImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {existingImages.map((imgUrl, idx) => {
                  if (removedExistingImages.includes(idx)) return null;
                  return (
                    <div key={idx} className="relative aspect-[16/9] rounded-xl overflow-hidden border border-gray-200 group">
                      <img src={imgUrl} alt="Arena" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setRemovedExistingImages((prev) => [...prev, idx])}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Hapus foto"
                      >
                        <Icon icon="ph:trash" className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Upload Button */}
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) {
                  setNewImageFiles(Array.from(e.target.files));
                }
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-10 px-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100/60 text-xs font-semibold text-gray-600 flex items-center justify-center gap-2 transition-colors cursor-pointer"
            >
              <Icon icon="ph:cloud-arrow-up-bold" className="w-4 h-4 text-gray-500" />
              <span>
                {newImageFiles.length > 0
                  ? `${newImageFiles.length} foto baru dipilih`
                  : 'Unggah Foto Tambahan'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Save Action */}
      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs font-semibold px-6 py-2.5 rounded-full transition-all cursor-pointer shadow-2xs active:scale-[0.98] disabled:opacity-50"
        >
          {isSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
        </button>
      </div>
    </form>
  );
};

export default OperasionalTab;
